<template>
  <div class="mou-moa-page">

    <!-- ── Toolbar ── -->
    <div class="toolbar-wrap mb-4">
      <!-- Row 1: filters -->
      <div class="toolbar-filters">
        <div class="filter-group filter-search">
          <label>Search</label>
          <div class="search-wrap">
            <i class="bi bi-search"></i>
            <input
              v-model="searchQuery"
              type="search"
              class="form-control"
              placeholder="Title, counterpart, or purpose…"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="filter-group">
          <label>Date Signed From</label>
          <input v-model="filterDateFrom" type="date" class="form-control" />
        </div>

        <div class="filter-group">
          <label>Date Signed To</label>
          <input v-model="filterDateTo" type="date" class="form-control" />
        </div>

        <div class="filter-group">
          <label>Type</label>
          <select v-model="filterType" class="form-control">
            <option value="">All types</option>
            <option value="mou">MOU</option>
            <option value="moa">MOA</option>
          </select>
        </div>

        <div class="filter-group">
          <label>CHED's Role</label>
          <select v-model="filterRole" class="form-control">
            <option value="">All roles</option>
            <option value="first_party">First Party</option>
            <option value="partner">Partner</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status</label>
          <select v-model="filterStatus" class="form-control">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="terminated">Terminated</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Year Signed</label>
          <select v-model="filterYear" class="form-control">
            <option value="">All years</option>
            <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Sort By</label>
          <select v-model="sortBy" class="form-control">
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="title_asc">Title A → Z</option>
            <option value="title_desc">Title Z → A</option>
            <option value="expiry_asc">Expiring soon</option>
          </select>
        </div>
      </div>

      <!-- Row 2: count + actions -->
      <div class="toolbar-meta">
        <span class="result-count">
          {{ sorted.length }} {{ sorted.length === 1 ? "document" : "documents" }}
          <span v-if="hasActiveFilters" class="clear-link" @click="clearFilters">· Clear filters</span>
        </span>
        <div class="toolbar-actions">
          <div class="view-toggle" role="group">
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: viewMode === 'list' }"
              title="List view"
              @click="viewMode = 'list'"
            >
              <i class="bi bi-list-ul"></i>
            </button>
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: viewMode === 'grid' }"
              title="Grid view"
              @click="viewMode = 'grid'"
            >
              <i class="bi bi-grid-3x3-gap"></i>
            </button>
          </div>
          <button
            v-if="canManage"
            type="button"
            class="btn btn-primary"
            @click="$refs.createModal.openCreate()"
          >
            <i class="bi bi-plus-lg mr-1"></i> Add Document
          </button>
        </div>
      </div>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <span class="spinner-border spinner-border-sm mr-2"></span>
      Loading documents…
    </div>

    <!-- ── Empty ── -->
    <div v-else-if="!sorted.length" class="empty-state">
      <i class="bi bi-file-earmark-text empty-icon"></i>
      <p class="mb-1">No MOU/MOA documents match your filters.</p>
      <span v-if="hasActiveFilters" class="clear-link" @click="clearFilters">Clear all filters</span>
    </div>

    <!-- ── LIST VIEW ── -->
    <div v-else-if="viewMode === 'list'" class="table-responsive list-view-wrap">
      <table class="table table-hover list-table mb-0">
        <thead>
          <tr>
            <th style="width:70px">Type</th>
            <th>Title / Counterpart</th>
            <th style="width:130px">CHED's Role</th>
            <th style="width:110px">Date Signed</th>
            <th style="width:110px">Expiry Date</th>
            <th style="width:100px">Status</th>
            <th style="width:80px">Uploaded by</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="doc in sorted"
            :key="doc.id"
            class="list-row"
            @click="openDetail(doc)"
            role="button"
            tabindex="0"
            @keydown.enter="openDetail(doc)"
          >
            <td>
              <span class="type-badge" :class="'type-' + doc.document_type">
                {{ doc.document_type.toUpperCase() }}
              </span>
            </td>
            <td>
              <div class="list-title">{{ doc.title }}</div>
              <div class="list-counterpart">
                <i class="bi bi-building mr-1"></i>{{ doc.counterpart }}
              </div>
              <div v-if="doc.purpose" class="list-purpose">{{ doc.purpose }}</div>
            </td>
            <td>
              <span class="role-pill">{{ partyRoleShort(doc.party_role) }}</span>
            </td>
            <td class="text-nowrap small">{{ doc.date_signed | shortDate }}</td>
            <td class="text-nowrap small" :class="{ 'text-danger font-weight-bold': isExpiringSoon(doc) }">
              {{ doc.expiry_date ? (doc.expiry_date | shortDate) : '—' }}
            </td>
            <td>
              <span class="status-badge" :class="'status-' + doc.status">
                {{ statusLabel(doc.status) }}
              </span>
            </td>
            <td class="small text-muted text-nowrap">
              {{ doc.uploader ? uploaderName(doc.uploader) : '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── GRID VIEW ── -->
    <div v-else class="doc-grid">
      <article
        v-for="doc in sorted"
        :key="doc.id"
        class="doc-card"
        @click="openDetail(doc)"
        role="button"
        tabindex="0"
        @keydown.enter="openDetail(doc)"
        @keydown.space.prevent="openDetail(doc)"
      >
        <div class="doc-card-top">
          <span class="type-badge" :class="'type-' + doc.document_type">
            {{ doc.document_type.toUpperCase() }}
          </span>
          <span class="status-badge" :class="'status-' + doc.status">
            {{ statusLabel(doc.status) }}
          </span>
          <span class="role-pill">{{ partyRoleShort(doc.party_role) }}</span>
        </div>

        <h3 class="doc-title">{{ doc.title }}</h3>

        <div class="doc-meta">
          <span class="meta-item">
            <i class="bi bi-building mr-1"></i>{{ doc.counterpart }}
          </span>
          <span class="meta-item">
            <i class="bi bi-calendar-event mr-1"></i>{{ doc.date_signed | shortDate }}
          </span>
          <span v-if="doc.expiry_date" class="meta-item" :class="{ 'text-danger': isExpiringSoon(doc) }">
            <i class="bi bi-calendar-x mr-1"></i>Exp: {{ doc.expiry_date | shortDate }}
          </span>
          <span v-if="doc.uploader" class="meta-item">
            <i class="bi bi-person mr-1"></i>{{ uploaderName(doc.uploader) }}
          </span>
        </div>

        <p v-if="doc.purpose" class="doc-purpose">{{ doc.purpose }}</p>

        <div class="doc-card-footer">
          <span v-if="doc.path" class="pdf-hint">
            <i class="bi bi-file-pdf mr-1"></i>PDF available
          </span>
          <span v-else class="pdf-hint text-muted">
            <i class="bi bi-file-earmark mr-1"></i>No PDF
          </span>
          <span class="click-hint"><i class="bi bi-arrow-right-circle mr-1"></i>Click to open</span>
        </div>
      </article>
    </div>

    <CreateMouMoaModal ref="createModal" @saved="load(false)" />
    <MouMoaDetailModal ref="detailModal" @edit="openEditFromModal" @deleted="load(false)" />
  </div>
</template>

<script>
import CreateMouMoaModal from "./CreateMouMoaModal.vue";
import MouMoaDetailModal from "./MouMoaDetailModal.vue";

export default {
  name: "MouMoaList",
  components: { CreateMouMoaModal, MouMoaDetailModal },
  filters: {
    shortDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  data() {
    return {
      loading: false,
      documents: [],
      viewMode: "list",
      filterType: "",
      filterRole: "",
      filterStatus: "",
      filterYear: "",
      filterDateFrom: "",
      filterDateTo: "",
      sortBy: "date_desc",
      searchQuery: "",
    };
  },
  computed: {
    canManage() {
      const perms = this.$store.state.permissions || [];
      if (perms.includes("mou_moa_manage")) return true;
      const role = this.$store.state.user && this.$store.state.user.role;
      return ["Administrator", "RD", "Chief", "Supervisor", "Records"].includes(role);
    },
    availableYears() {
      const years = this.documents
        .map((d) => d.date_signed ? new Date(d.date_signed).getFullYear() : null)
        .filter(Boolean);
      return [...new Set(years)].sort((a, b) => b - a);
    },
    hasActiveFilters() {
      return this.filterType || this.filterRole || this.filterStatus || this.filterYear ||
             this.filterDateFrom || this.filterDateTo || this.searchQuery;
    },
    filtered() {
      let list = this.documents;
      if (this.filterType) list = list.filter((d) => d.document_type === this.filterType);
      if (this.filterRole) list = list.filter((d) => d.party_role === this.filterRole);
      if (this.filterStatus) list = list.filter((d) => d.status === this.filterStatus);
      if (this.filterYear) {
        list = list.filter((d) => {
          if (!d.date_signed) return false;
          return new Date(d.date_signed).getFullYear() === Number(this.filterYear);
        });
      }
      if (this.filterDateFrom) {
        const from = new Date(this.filterDateFrom);
        list = list.filter((d) => d.date_signed && new Date(d.date_signed) >= from);
      }
      if (this.filterDateTo) {
        const to = new Date(this.filterDateTo);
        list = list.filter((d) => d.date_signed && new Date(d.date_signed) <= to);
      }
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (q) {
        list = list.filter(
          (d) =>
            (d.title || "").toLowerCase().includes(q) ||
            (d.counterpart || "").toLowerCase().includes(q) ||
            (d.purpose || "").toLowerCase().includes(q)
        );
      }
      return list;
    },
    sorted() {
      const list = [...this.filtered];
      switch (this.sortBy) {
        case "date_asc":
          return list.sort((a, b) => new Date(a.date_signed) - new Date(b.date_signed));
        case "title_asc":
          return list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        case "title_desc":
          return list.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
        case "expiry_asc":
          return list.sort((a, b) => {
            if (!a.expiry_date) return 1;
            if (!b.expiry_date) return -1;
            return new Date(a.expiry_date) - new Date(b.expiry_date);
          });
        default:
          return list.sort((a, b) => new Date(b.date_signed) - new Date(a.date_signed));
      }
    },
  },
  methods: {
    statusLabel(status) {
      return { active: "Active", expired: "Expired", terminated: "Terminated" }[status] || status;
    },
    partyRoleShort(role) {
      return { first_party: "1st Party", partner: "Partner" }[role] || role;
    },
    uploaderName(uploader) {
      if (!uploader) return "—";
      return [uploader.first_name, uploader.last_name].filter(Boolean).join(" ");
    },
    isExpiringSoon(doc) {
      if (!doc.expiry_date || doc.status !== "active") return false;
      const diff = new Date(doc.expiry_date) - new Date();
      return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000;
    },
    clearFilters() {
      this.filterType = "";
      this.filterRole = "";
      this.filterStatus = "";
      this.filterYear = "";
      this.filterDateFrom = "";
      this.filterDateTo = "";
      this.searchQuery = "";
    },
    openDetail(doc) {
      this.$refs.detailModal.show(doc);
    },
    openEditFromModal(doc) {
      $("#mou-moa-detail-modal").modal("hide");
      this.$nextTick(() => {
        this.$refs.createModal.openEdit(doc);
      });
    },
    load(toggle = true) {
      this.loading = toggle;
      axios
        .get("api/mou-moa")
        .then((res) => {
          this.documents = res.data || [];
        })
        .catch(() => {
          toastr.error("Failed to load documents.", "Error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
/* ── Toolbar ── */
.toolbar-wrap { display: flex; flex-direction: column; gap: .9rem; }
.toolbar-filters {
  display: flex;
  flex-wrap: wrap;
  gap: .65rem;
  align-items: flex-end;
}
.toolbar-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: .5rem;
}
.toolbar-actions { display: flex; align-items: center; gap: .6rem; }

.filter-group label {
  display: block;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .35px;
  text-transform: uppercase;
  color: #768192;
  margin-bottom: .3rem;
}
.filter-group .form-control {
  min-width: 140px;
  border-radius: 10px;
  min-height: 40px;
  border-color: rgba(0,0,21,.1);
}
.filter-search .search-wrap { position: relative; }
.filter-search .search-wrap i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #768192;
  pointer-events: none;
}
.filter-search { flex: 1 1 400px; }
.filter-search input {
  padding-left: 2.2rem;
  width: 100%;
  border-radius: 10px;
  min-height: 40px;
  border-color: rgba(0,0,21,.1);
}

.result-count { font-size: .88rem; color: #768192; }
.clear-link {
  color: #4f46e5;
  cursor: pointer;
  font-size: .82rem;
  text-decoration: underline;
  margin-left: .25rem;
}

/* ── View toggle ── */
.view-toggle {
  display: inline-flex;
  border: 1px solid rgba(0,0,21,.12);
  border-radius: 10px;
  overflow: hidden;
}
.toggle-btn {
  background: transparent;
  border: none;
  padding: .38rem .65rem;
  cursor: pointer;
  font-size: .95rem;
  color: #768192;
  transition: background .15s, color .15s;
  line-height: 1;
}
.toggle-btn.active { background: #4f46e5; color: #fff; }
.toggle-btn:not(.active):hover { background: rgba(0,0,21,.05); }

/* ── List view ── */
.list-view-wrap {
  background: #fff;
  border: 1px solid rgba(0,0,21,.07);
  border-radius: 14px;
  overflow: hidden;
}
.list-table thead th {
  background: #f8f9fc;
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .35px;
  color: #768192;
  border-bottom: 1px solid rgba(0,0,21,.08);
  padding: .65rem 1rem;
  white-space: nowrap;
}
.list-table tbody td {
  padding: .75rem 1rem;
  vertical-align: middle;
  border-color: rgba(0,0,21,.05);
}
.list-table tbody tr:last-child td { border-bottom: none; }
.list-row { cursor: pointer; }
.list-row:hover td { background: rgba(79,70,229,.03); }
.list-title { font-size: .9rem; font-weight: 600; color: #2b3a55; line-height: 1.4; }
.list-counterpart { font-size: .78rem; color: #768192; margin-top: .15rem; }
.list-purpose {
  font-size: .75rem;
  color: #8a97ab;
  margin-top: .1rem;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── Grid view ── */
.doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 1rem;
}
.doc-card {
  background: #fff;
  border: 1px solid rgba(0,0,21,.06);
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  box-shadow: 0 4px 18px rgba(21,38,63,.04);
  transition: box-shadow .18s, transform .18s, border-color .18s;
  cursor: pointer;
  outline: none;
}
.doc-card:hover, .doc-card:focus {
  box-shadow: 0 8px 28px rgba(21,38,63,.1);
  transform: translateY(-2px);
  border-color: rgba(79,70,229,.25);
}
.doc-card-top { display: flex; align-items: center; gap: .5rem; margin-bottom: .65rem; flex-wrap: wrap; }
.doc-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: .75rem;
  padding-top: .65rem;
  border-top: 1px solid rgba(0,0,21,.05);
}
.pdf-hint { font-size: .78rem; color: #4d8dff; }
.click-hint { font-size: .75rem; color: #a0aec0; }

/* ── Shared badges ── */
.type-badge {
  display: inline-block;
  padding: .22rem .55rem;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 800;
  letter-spacing: .4px;
}
.type-badge.type-mou { background: rgba(77,141,255,.12); color: #4d8dff; }
.type-badge.type-moa { background: rgba(43,182,163,.12); color: #1f8f7f; }

.status-badge {
  display: inline-block;
  padding: .18rem .5rem;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  white-space: nowrap;
}
.status-badge.status-active   { background: rgba(43,182,115,.12); color: #1a7a4a; }
.status-badge.status-expired  { background: rgba(224,144,31,.12);  color: #c97812; }
.status-badge.status-terminated { background: rgba(200,60,60,.1);   color: #b03030; }

.role-pill {
  display: inline-block;
  padding: .18rem .48rem;
  border-radius: 999px;
  font-size: .67rem;
  font-weight: 700;
  background: rgba(79,70,229,.09);
  color: #4f46e5;
  white-space: nowrap;
}

.doc-title { font-size: .97rem; font-weight: 600; color: #2b3a55; line-height: 1.45; margin-bottom: .6rem; }
.doc-meta { display: flex; flex-wrap: wrap; gap: .35rem .9rem; margin-bottom: .6rem; }
.meta-item { font-size: .8rem; color: #768192; }
.doc-purpose {
  font-size: .84rem;
  color: #768192;
  margin-bottom: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #768192;
  background: #fff;
  border-radius: 14px;
  border: 1px dashed rgba(0,0,21,.1);
}
.empty-icon { font-size: 2rem; display: block; margin-bottom: .5rem; opacity: .45; }
</style>
